// @atlas/contracts — a `invoices` modul Zod-sémái
//
// A fájl CSAK sémákat tart (érték-szintű Zod-objektumok + a belőlük inferált típusok) — a ROUTE-ok
// egyetlen `atlasContract` objektum-literálban maradnak (`src/index.ts`), mert a ts-rest
// kontextus-inferenciája a lapos bővítésre érzékeny. A sémák mozgatása ezt NEM érinti: sima értékek.
import { INVOICE_CURRENCIES, INVOICE_DELIVERY_TYPES, INVOICE_ISSUER_KEYS, INVOICE_PAYMENT_METHODS, INVOICE_QUANTITY_UNITS, INVOICE_TYPES, INVOICE_VAT_RATES, } from '@atlas/shared';
import { z } from 'zod';
import { isoDateStr, nullableStr } from '../_shared/schemas.js';
/**
 * Számla-sor (billing lista-nézet, belső): a `billing.invoices` [T] busz-erőforrás fehérlistás mezői
 * (invoiceNumber/status/type/currency/összegek/dátumok) + a kapcsolt entitások BFF-oldali feloldása:
 * kibocsátó (`issuerKey` → `issuerName` snapshot, fallback a kulcs), és a számlázott fiók(ok) neve
 * (`billing.invoice-accounts` pivot → `billing.invoices.accountId` → foundation `accounts` név-feloldás).
 * A pénz-értékek az Odinból Decimal→string formában érkeznek — a szerződés ezért STRING-ként hordozza
 * őket (nem number, hogy a pontosság ne vesszen el); a megjelenítés-formázás (HUF egész / más deviza 2
 * tizedes, ezres-tagolás) a UI dolga. A `legacyId` sosem jön. A tétel-részletnézet MÉG NINCS (a v1 lista).
 */
export const InvoiceRowSchema = z.object({
    id: z.string(),
    clientId: z.string(),
    accountId: z.string().nullable(),
    /** A számlázott fiók(ok) neve (a pivot `invoice-accounts` + a saját `accountId` alapján, feloldva). */
    accountNames: z.array(z.string()),
    invoiceNumber: z.string().nullable(),
    /** A kibocsátó kulcsa (none | MOR | MDIG). */
    issuerKey: z.string().nullable(),
    /** A kibocsátó ember-olvasható neve (a snapshot `issuerName`; fallback az `issuerKey`). */
    issuerName: z.string().nullable(),
    // ── A SNAPSHOT-mezők a LISTÁN is ─────────────────────────────────────────────────────────
    // A „Számlázott fél" és a „Kibocsátó" oszlop a lista-konvenció szerint HÁROM sort mutat (név · cím ·
    // adószám-chipek), és mindhárom adat MÁR a számlán van (jogi snapshot a kiállítás pillanatából) — eddig
    // csak a lista-szerződés nem hordozta, ezért a két oszlop egy-egy csupasz név volt. NEM új Odin-mező:
    // ugyanannak a busz-sornak a további fehérlistás oszlopai.
    /** A VEVŐ neve a számlán (snapshot) — a fiók-nevek MELLETT ez a számlázott fél jogi neve. */
    billingName: z.string().nullable(),
    billingZipCode: z.string().nullable(),
    billingCity: z.string().nullable(),
    billingAddress: z.string().nullable(),
    /** ISO-3166 alpha-2 — a `LabelChip` felirata az adószám előtt (HU/DE/…). */
    billingCountryCode: z.string().nullable(),
    billingTaxNumber: z.string().nullable(),
    billingEuTaxNumber: z.string().nullable(),
    issuerZipCode: z.string().nullable(),
    issuerCity: z.string().nullable(),
    issuerAddress: z.string().nullable(),
    issuerCountryCode: z.string().nullable(),
    issuerTaxNumber: z.string().nullable(),
    issuerEuTaxNumber: z.string().nullable(),
    /** invoice | proforma. */
    type: z.string().nullable(),
    /** electronic | paper — a típussal együtt adja a bizonylat fajtáját (`invoiceDocKind`: e-számla / számla / …). */
    deliveryType: z.string().nullable(),
    /** draft | issued | paid | overdue | cancelled | storno. */
    status: z.string(),
    /** ISO-4217 devizakód (HUF | EUR | USD) — a pénz-formázás ez alapján dönt egész/2-tizedes. */
    currency: z.string(),
    /** Decimal→string (a pontosság megőrzéséért). Nettó összeg. */
    netTotal: z.string().nullable(),
    /** Decimal→string. ÁFA összeg. */
    vatTotal: z.string().nullable(),
    /** Decimal→string. Bruttó (végösszeg). */
    grossTotal: z.string().nullable(),
    /** Kiállítás dátuma (ISO date) — a busz `issueDate`-je. */
    issueDate: z.string().nullable(),
    /**
     * Teljesítés dátuma (ISO date). A LISTÁN azért kell, mert a kiállítás megerősítő ablaka a HÁROM dátumot
     * előre kitöltve mutatja (a kiállítás napjára igazítva) — a lista-sorról is indítható a kiállítás.
     */
    fulfillmentDate: z.string().nullable(),
    /** Fizetési határidő (ISO date). */
    dueDate: z.string().nullable(),
    /** Fizetés dátuma (ISO date) — a Számlázz.hu státusz-szinkron írja, ha a számlát kifizették. A legacy
     *  lista dátum-csoportja is tartalmazta (`list.payment_date`); nem fizetett bizonylaton null. */
    paymentDate: z.string().nullable(),
    /** Sztornó (fordított) számla száma — a Számlázz.hu-tól, ha a számlát sztornózták (különben null). */
    stornoInvoiceNumber: z.string().nullable(),
    /** Kapcsolódó számla száma (pl. díjbekérőből autokasszával generált számla) — különben null. */
    relatedInvoiceNumber: z.string().nullable(),
    /** OCC-verzió (a busz-sor `version`-je) — a sor-akciók (szerkesztés/törlés) If-Match forrása. */
    version: z.number(),
});
/** Számla-lista lekérdezés: státusz-szűrő + szabad-szöveg (számlaszám) + szerver-rendezés (sort/order) +
 *  offset-lapozás (a BFF-szűrt halmazon). A `sort` a rendezés-kulcs (issueDate | dueDate | invoiceNumber |
 *  issuerName | grossTotal | status) — a BFF whitelisttel validál (ismeretlen → issueDate). */
export const InvoiceListQuerySchema = z.object({
    /**
     * SZABAD-SZÖVEG kereső. A BFF a számlaszámon KÍVÜL a számlázott fél és a kibocsátó NEVÉRE és CÍMÉRE, valamint
     * az ÖSSZEGEKRE is keres (nettó/áfa/bruttó) — user-döntés. Miért BFF-oldalon: a busz ezen a
     * tipizált táblán nem ad szerver-oldali szabad-szöveges keresést, a lista pedig amúgy is teljes állományt
     * lapoz (a szűrés/rendezés is itt van).
     */
    q: z.string().optional(),
    /**
     * Státusz-szűrő: egy KONKRÉT státusz (`draft`/`issued`/…) VAGY egy legacy FÜL-CSOPORT
     * (`group:issued` = kiállítva + lejárt · `group:void` = sztornózott + érvénytelenített). A legacy lista négy
     * fülre osztotta a bizonylatokat, és a „Kiállított" fülön a LEJÁRT sorok is ott voltak (vizuálisan
     * megkülönböztetve) — a csoport-értékek ezt a nézetet tartják meg a szűrő-alapú felületen is.
     */
    status: z.string().optional(),
    /**
     * A lista első szűrője: a BIZONYLAT FAJTÁJA (`e_invoice` · `paper_invoice` · `proforma` · `storno` — a közös
     * `invoiceDocKind` szabállyal, a jelvénnyel azonosan), vagy a nyers típus (`invoice` = minden számla).
     */
    type: z.string().optional(),
    /** Kibocsátó-szűrő (a soron lévő `issuerKey`: none | MOR | MDIG) — a legacy „számlatömb" (InvoicePrefixEnum). */
    issuer: z.string().optional(),
    /** KIÁLLÍTÁS dátuma ettől/eddig (`YYYY-MM-DD`, inklúzív) — a legacy lista dátum-szűrője. */
    issuedFrom: z.string().optional(),
    issuedTo: z.string().optional(),
    /** FIZETÉSI HATÁRIDŐ ettől/eddig (`YYYY-MM-DD`, inklúzív). */
    dueFrom: z.string().optional(),
    dueTo: z.string().optional(),
    sort: z.string().optional(),
    order: z.enum(['asc', 'desc']).optional(),
    limit: z.coerce.number().int().positive().max(200).optional(),
    offset: z.coerce.number().int().nonnegative().optional(),
});
/** DATE-only dátum (YYYY-MM-DD) — a számla-dátummezők drót-formátuma (nincs időzóna-csúszás). */
const invoiceDateStr = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'YYYY-MM-DD');
/** Nem-negatív Decimal-string, max 2 tizedes (pl. 12000 vagy 12000.50). */
const invoiceDecimalStr = z
    .string()
    .regex(/^\d+(\.\d{1,2})?$/, 'Decimal-string (pl. 12000 vagy 12000.50)');
/**
 * Egy beküldött számla-tétel (a TELJES tétel-lista a PUT-ban is egyben utazik — a BFF diff-el).
 * Az összeg-mezők (netPrice/vatAmount/grossPrice) SZÁNDÉKOSAN nincsenek a sémában — a szerver számol.
 */
export const InvoiceItemInputSchema = z.object({
    /** Meglévő tétel id-ja (a diff-kulcs); hiányzik → új sor; a listából kimaradt id → törlés. */
    id: z.string().uuid().optional(),
    /**
     * A tétel KATALÓGUS-csomagja — KÖTELEZŐ: szabad szöveges, besorolatlan tétel nincs (a kimutatás a csomag
     * kategóriája szerint sorol). A BFF ellenőrzi, hogy létező csomag; a generált tételé nem cserélhető.
     */
    servicePlanId: z.string().uuid(),
    /** A számlán megjelenő név — a csomagból előtöltve, de átírható (a besorolás nem a szövegből jön). */
    name: z.string().trim().min(1).max(500),
    quantity: invoiceDecimalStr.refine((v) => Number(v) >= 0.01, 'Legalább 0.01'),
    quantityUnit: z.enum(INVOICE_QUANTITY_UNITS),
    unitPrice: invoiceDecimalStr,
    vatRate: z.enum(INVOICE_VAT_RATES),
    note: nullableStr(1000),
});
/**
 * Számla-scope: VAGY fiók-szintű (accountId), VAGY ügyfél-szintű összevont (accountIds, min 1 —
 * a fiókok a `billing/invoice-accounts` pivotba kerülnek). Az ügyfél draft-on utólag NEM váltható.
 */
export const InvoiceScopeSchema = z.discriminatedUnion('kind', [
    z.object({ kind: z.literal('account'), accountId: z.string().uuid() }),
    z.object({
        kind: z.literal('client'),
        accountIds: z.array(z.string().uuid()).min(1).max(200),
    }),
]);
/**
 * Draft-számla teljes írás-body (create + update közös törzs). A kliens CSAK `issuerKey`-t küld — az
 * `issuer_*` snapshotot a BFF másolja a `billing/issuers` törzsből; státuszt/összegeket nem küldhet.
 * BFF-szabályok (422): minden tétel `vatRate`-je azonos (ÁFA-lock, mező-szintű hiba a sértő indexre);
 * EUFAD37 → `billingEuTaxNumber` kötelező; a scope-fiókok a cél-ügyfélhez tartoznak; minden tétel `servicePlanId`-je
 * létező katalógus-csomag, és a generált tétel csomagja nem cserélhető.
 */
export const InvoiceDraftBodySchema = z.object({
    scope: InvoiceScopeSchema,
    issuerKey: z.enum(INVOICE_ISSUER_KEYS),
    type: z.enum(INVOICE_TYPES),
    deliveryType: z.enum(INVOICE_DELIVERY_TYPES),
    paymentMethod: z.enum(INVOICE_PAYMENT_METHODS),
    currency: z.enum(INVOICE_CURRENCIES).default('HUF'),
    fulfillmentDate: invoiceDateStr,
    issueDate: invoiceDateStr,
    dueDate: invoiceDateStr,
    billingName: z.string().trim().min(1).max(255),
    billingTaxNumber: nullableStr(50),
    billingEuTaxNumber: nullableStr(50),
    billingCountryCode: z.string().trim().min(2).max(2),
    billingZipCode: z.string().trim().min(1).max(20),
    billingCity: z.string().trim().min(1).max(255),
    billingAddress: z.string().trim().min(1).max(500),
    billingEmails: z.array(z.string().email().max(255)).max(10),
    /**
     * SZÁMLA-MEGJEGYZÉS — EGY mező (user-döntés; korábban fejléc+lábléc kettős volt).
     * Ez megy a Számlázz.hu felé a bizonylat megjegyzésébe is.
     */
    note: nullableStr(5000),
    items: z.array(InvoiceItemInputSchema).min(1).max(200),
});
/** Create-body: + cél-ügyfél (CSAK create-nél — update/delete a számla-sor tenantjából dolgozik). */
export const InvoiceCreateBodySchema = InvoiceDraftBodySchema.extend({
    clientId: z.string().uuid(),
});
/** A szerkesztő-picker adatai. `?clientId=` nélkül az `accounts`/`billingProfiles` üres. */
export const InvoiceRefsSchema = z.object({
    /** Ügyfél-választó (create-kor) — foundation `/v1/admin/clients` (id + név). */
    clients: z.array(z.object({ id: z.string(), name: z.string() })),
    /** Kibocsátó-törzs (`billing/issuers` [SHARED]) — a kibocsátó-adat előnézethez is. */
    issuers: z.array(z.object({
        key: z.string(),
        name: z.string().nullable(),
        taxNumber: z.string().nullable(),
        euTaxNumber: z.string().nullable(),
        countryCode: z.string().nullable(),
        zipCode: z.string().nullable(),
        city: z.string().nullable(),
        address: z.string().nullable(),
        bankName: z.string().nullable(),
        bankAccount: z.string().nullable(),
    })),
    /** A választott ügyfél fiókjai (a client-scope multiselect; default: minden AKTÍV fiók kijelölve). */
    accounts: z.array(z.object({ id: z.string(), name: z.string(), status: z.string() })),
    /** Számlázási profilok — CSAK elő-kitöltéshez (a profil-id NEM tárolódik a számlán). */
    billingProfiles: z.array(z.object({
        id: z.string(),
        isPrimary: z.boolean(),
        billingName: z.string().nullable(),
        taxNumber: z.string().nullable(),
        euTaxNumber: z.string().nullable(),
        countryCode: z.string().nullable(),
        zipCode: z.string().nullable(),
        city: z.string().nullable(),
        address: z.string().nullable(),
        billingEmails: z.array(z.string()),
        defaultInvoicePaymentDeadlineDays: z.number().nullable(),
        /** A SZÁMLA két megjegyzés-mezőjének elő-kitöltése (a profil ugyanígy két mezőt tart). */
        defaultInvoiceNote: z.string().nullable(),
    })),
    /**
     * A tétel-választó: a KATALÓGUS csomagjai (nem törölt), szolgáltatás- majd csomagnév szerint rendezve. A
     * választás előtölti a tétel nevét, megjegyzését, ÁFA-kulcsát és — fix áras csomagnál — az egységárát.
     */
    servicePlans: z.array(z.object({
        id: z.string(),
        serviceName: z.string(),
        planName: z.string(),
        /** A számlán megjelenő név (a csomag `invoiceItemName`-je, különben a csomag neve). */
        itemName: z.string(),
        itemNote: z.string().nullable(),
        mainCategory: z.string().nullable(),
        billingType: z.string().nullable(),
        vatRate: z.string().nullable(),
        /** A fix áras (`flat`) csomag egységára; minden más árazásnál null (nincs egyetlen egységár). */
        unitPrice: z.object({ netPrice: z.string(), currency: z.string() }).nullable(),
    })),
});
export const InvoiceRefsQuerySchema = z.object({ clientId: z.string().uuid().optional() });
/** Egy tétel a detail-nézetben: form-mezők + tétel-OCC + a szerver-számított összegek. */
export const InvoiceItemDetailSchema = z.object({
    id: z.string(),
    version: z.number(),
    /** A katalógus-csomag — null csak egy csomag nélküli régi soron (szerkesztéskor választani kell). */
    servicePlanId: z.string().nullable(),
    /** A forrás-hozzárendelés: generált tételnél kitöltött — ilyenkor a csomag a szerkesztőben zárolt. */
    servicePlanAssignmentId: z.string().nullable(),
    name: z.string(),
    quantity: z.string(),
    quantityUnit: z.string().nullable(),
    unitPrice: z.string(),
    vatRate: z.string(),
    note: z.string().nullable(),
    netPrice: z.string(),
    vatAmount: z.string(),
    grossPrice: z.string(),
});
/**
 * Számla-detail: a teljes fej (form-mezők + fej-OCC `version`) + tétel-lista + a client-scope
 * `accountIds` (a pivotból) — a szerkesztő ÉS a read-only nézet közös forrása.
 */
export const InvoiceDetailSchema = z.object({
    id: z.string(),
    clientId: z.string(),
    accountId: z.string().nullable(),
    /** Client-scope fiók-lista (a `billing/invoice-accounts` pivot `targetAccountId`-jai). */
    accountIds: z.array(z.string()),
    invoiceNumber: z.string().nullable(),
    status: z.string(),
    version: z.number(),
    issuerKey: z.string().nullable(),
    type: z.string().nullable(),
    deliveryType: z.string().nullable(),
    paymentMethod: z.string().nullable(),
    currency: z.string(),
    fulfillmentDate: z.string().nullable(),
    issueDate: z.string().nullable(),
    dueDate: z.string().nullable(),
    billingName: z.string().nullable(),
    billingTaxNumber: z.string().nullable(),
    billingEuTaxNumber: z.string().nullable(),
    billingCountryCode: z.string().nullable(),
    billingZipCode: z.string().nullable(),
    billingCity: z.string().nullable(),
    billingAddress: z.string().nullable(),
    billingEmails: z.array(z.string()),
    // ── KIBOCSÁTÓ-SNAPSHOT (a `billing/issuers` törzsből a kiállításkor lemásolva) ────────────────────────
    //
    // MIÉRT A SNAPSHOT megy ki, és miért nem a UI oldja fel a törzsből: (1) a számla JOGI bizonylat — azt az
    // adatot kell mutatnia, ami RAJTA van, nem a kibocsátó MAI címét/bankszámláját (a törzs változhat a
    // kiállítás után); (2) a törzs-lekérő végpont (`invoiceRefs`) `invoices.manage`-gated, tehát CSAK-VIEW
    // joggal a nézet korábban a kulcs-címkére és darabszámra degradált. A snapshot mindkettőt megoldja:
    // hiteles ÉS `view` jog alatt is teljes.
    issuerName: z.string().nullable(),
    issuerTaxNumber: z.string().nullable(),
    issuerEuTaxNumber: z.string().nullable(),
    issuerCountryCode: z.string().nullable(),
    issuerZipCode: z.string().nullable(),
    issuerCity: z.string().nullable(),
    issuerAddress: z.string().nullable(),
    issuerBankName: z.string().nullable(),
    issuerBankAccount: z.string().nullable(),
    /**
     * A SZÁMLÁZOTT fiókok NÉVVEL (client-scope pivot vagy account-scope egyetlen fiók) — a szerver oldja fel a
     * foundation fiók-listából. Korábban a UI a manage-gated picker-végpontból merge-elte, ezért `view` jog
     * alatt csak darabszám látszott. Feloldhatatlan id → a lista RÖVIDEBB, mint az `accountIds` (nyers UUID-t
     * nem írunk a felületre).
     */
    billedAccounts: z.array(z.object({ id: z.string(), name: z.string() })),
    /** Számla-megjegyzés (egy mező — lásd a write-body `note`-ját). */
    note: z.string().nullable(),
    netTotal: z.string().nullable(),
    vatTotal: z.string().nullable(),
    grossTotal: z.string().nullable(),
    /** Fizetés dátuma (a Számlázz.hu státusz-szinkronból), ha kifizetett — különben null. */
    paymentDate: z.string().nullable(),
    /** Sztornó (fordított) számla száma — sztornózott számlánál, különben null. */
    stornoInvoiceNumber: z.string().nullable(),
    /** Kapcsolódó (pl. díjbekérőből autokasszával generált) számla száma — különben null. */
    relatedInvoiceNumber: z.string().nullable(),
    /** Sztornó-megjegyzés (a sztornózáskor megadott opcionális szöveg) — különben null. */
    stornoComment: z.string().nullable(),
    items: z.array(InvoiceItemDetailSchema),
});
/** A sztornó opcionális megjegyzése. */
/** TÖMEGES kiállítás bemenete: a kijelölt számla-id-k (a UI a betöltött lapból ad kijelölést). */
export const InvoiceBulkIssueBodySchema = z.object({
    /** Legalább 1, legfeljebb 100 id — a plafon védi a Számlázz.hu-hívások sorozatát (a legacy is lapon dolgozott). */
    ids: z.array(z.string().uuid()).min(1).max(100),
});
/**
 * TÖMEGES kiállítás eredménye. A `summary` a legacy toast-jának bontását tükrözi (számla/díjbekérő × siker/hiba),
 * a `results` pedig SORONKÉNT ad kimenetet — így a UI meg tudja mutatni, MELYIK sor min bukott el.
 */
export const InvoiceBulkIssueResultSchema = z.object({
    summary: z.object({
        successInvoice: z.number(),
        successProforma: z.number(),
        failedInvoice: z.number(),
        failedProforma: z.number(),
    }),
    results: z.array(z.object({
        id: z.string(),
        ok: z.boolean(),
        /** `invoice` | `proforma` — a bontáshoz; ismeretlen sornál `invoice` (a legacy default). */
        type: z.string(),
        /** Hiba esetén a GENERIKUS, whitelistelt üzenet (nyers upstream-szöveg SOHA nem szivárog). */
        message: z.string().nullable(),
    })),
});
export const InvoiceStornoBodySchema = z.object({
    stornoComment: nullableStr(1000),
});
/** Egy életciklus-művelet eredménye (a frissített fej — a UI a cache-t erre frissíti). */
export const InvoiceLifecycleResultSchema = z.object({
    id: z.string(),
    version: z.number(),
    status: z.string(),
    invoiceNumber: z.string().nullable(),
    stornoInvoiceNumber: z.string().nullable(),
});
/** A PDF-letöltés eredménye — a nyers PDF base64-ben (a UI Blob-bá dekódolja; SOHA nem megy fájlba). */
export const InvoicePdfSchema = z.object({
    filename: z.string(),
    contentBase64: z.string(),
});
/** Melyik PDF-et töltjük le: az alap számla (`invoice`, default) vagy a sztornó (`storno`). */
export const InvoicePdfQuerySchema = z.object({
    which: z.enum(['invoice', 'storno']).optional(),
});
/** A státusz-szinkron összegzése (hány számla frissült). */
export const InvoiceSyncResultSchema = z.object({
    scanned: z.number(),
    paid: z.number(),
    overdue: z.number(),
    related: z.number(),
});
/**
 * Havi számla-generátor bemenete (`POST /invoices/generate`). `month` = a számlázási hónap (`YYYY-MM`);
 * `types` = pricing-type szűrő (üres → mind); `dryRun` = csak becslés (nem ír a buszra). A legacy
 * `--issue` opció SZÁNDÉKOSAN KIMARAD — a generátor csak DRAFT-ot állít elő (a kiállítás külön flow).
 */
export const InvoiceGenerateBodySchema = z.object({
    month: z.string().regex(/^\d{4}-(0[1-9]|1[0-2])$/, 'YYYY-MM formátum kötelező'),
    types: z.array(z.enum(['flat', 'tiered', 'base_tiered', 'commission'])).optional(),
    dryRun: z.boolean().optional().default(false),
});
/**
 * A generátor KIHAGYÁS-OKAI — EGY helyen (a `pricing.ts`, a tervező, a válasz és a kihagyás-napló ugyanezt a
 * készletet használja; a UI a `invoices.generate.skipReason.<kód>` i18n-kulccsal fordítja).
 *
 * - `invalid_period` — a periódus nem számítható (hiányzó/rossz kezdő dátum, frekvencia);
 * - `already_invoiced` — ugyanarra a hozzárendelés+periódusra MÁR van tétel (normál idempotencia — a válaszban
 *   megjelenik, a NAPLÓBA NEM kerül, mert nem hiba);
 * - `no_usage_source` / `no_usage_data` — usage-alapú árazásnál nincs adatforrás / nincs havi adat;
 * - `no_rate` — devizás tételnél hiányzik a havi árfolyam;
 * - `no_price` — a hozzárendelésen nincs ár-sor;
 * - `zero` — a számított összeg 0 (nincs tétel) — ide tartozik a TELJESEN FEDETLEN záró periódus is (a
 *   szerződés a periódus kezdete előtt lejárt);
 * - `eu_tax_number_required` — EU-s fordított adózás EU-adószám nélkül (a csoport egésze kimarad);
 * - `issuer_missing` — a hozzárendelés kibocsátó-kulcsához NINCS sor a kibocsátó-katalógusban (a csoport
 *   egésze kimarad). Kibocsátó nélkül nincs érvényes bizonylat: se név, se adószám, se bankszámla nem
 *   kerülne rá. Korábban ez NEM volt megfogva — a számla üres kibocsátóval épült volna meg;
 * - `write_failed` — a busz-írás nem sikerült (nem-2xx, nem fojtás) — a piszkozat NEM készült el;
 * - `rate_limited` — a busz fojtott (429) — a piszkozat NEM készült el, újrafuttatás pótolja;
 * - `auto_billing_off` — az automata számlázás ki van kapcsolva az ügyfélen vagy a fiókon (a `details`
 *   mondja meg, melyiken) — a sor esedékes lett volna;
 * - `manually_removed` — a tételt EMBER törölte egy piszkozatról, ezért a generátor nem állítja elő újra
 *   (ZÁR; a napi futás következménye — lásd lent);
 * - `assignment_not_active` — a hozzárendelés saját státusza nem `active` (függő/inaktív);
 * - `owner_inactive` — a tulajdonos ügyfél vagy fiók INAKTÍV (nem a kapcsoló, hanem a CRM-státusz).
 */
export const INVOICE_GENERATION_SKIP_REASONS = [
    'invalid_period',
    'already_invoiced',
    'no_usage_source',
    'no_usage_data',
    'no_rate',
    'no_price',
    'zero',
    'eu_tax_number_required',
    'issuer_missing',
    'write_failed',
    'rate_limited',
    /**
     * A MÉRT usage-adat devizája ELTÉR a számla célydevizájától. Nem váltunk át: a szerződés nem ad hozzá
     * árfolyamot, a hozzárendelés ársora pedig a cél-devizát rögzíti. Átváltás helyett a tétel KIMARAD, és a
     * kihagyás-naplóban látszik — enélkül az összeg CÍMKÉT cserélt volna (1 234,56 EUR → 1 234,56 HUF).
     */
    'currency_mismatch',
    /**
     * AZ AUTOMATA SZÁMLÁZÁS KI VAN KAPCSOLVA az ügyfélen vagy a fiókon — a sor esedékes lett volna, de a
     * kapcsoló miatt kimarad.
     *
     * ⚠️ EZ KORÁBBAN NÉMA VOLT, és ez volt a baj: a generátor a kapunál `continue`-zott, tehát se számla, se
     * napló-bejegyzés nem keletkezett. Aki a kihagyás-naplót nézte, nem látta, hogy valami kimaradt — pedig
     * élesben **34 fiókon** volt kikapcsolva a kapcsoló, és ebből **28-nak volt aktív számlázás-sora**
     * (összesen 65 kieső sor, kb. 2,8 M Ft havi fix nettó). A kapcsoló kikapcsolása LEHET szándékos; a
     * NÉMASÁG nem az. A `details` megmondja, HOL van kikapcsolva (ügyfél vagy fiók), hogy egy kattintással
     * javítható legyen.
     */
    'auto_billing_off',
    /**
     * A TÉTELT EMBER TÖRÖLTE egy piszkozatról — a generátor NEM állítja elő újra („zár").
     *
     * ⚠️ EZ A NAPI GENERÁLÁS KÖVETKEZMÉNYE. Amíg havonta EGYSZER futott a generátor, egy fölöslegesnek ítélt
     * sor törlése végleges volt. A napi futás viszont másnap reggel visszatenné: a tétel eltűnésével a
     * [hozzárendelés, periódus] idempotencia-kulcs is felszabadul, tehát a generátor „hiányzó tételt" lát.
     * A kézi törlés ezért ZÁRAT tesz a (hónap · hozzárendelés · periódus) hármasra, és a zár LÁTSZIK a
     * kihagyás-naplóban — nem néma szabály, hanem egy sor, amit fel lehet oldani.
     *
     * A zár a többi októl eltérően NEM futás-termék: a generátor csak OLVASSA (soha nem írja és nem zárja le),
     * az írója a számla-szerkesztő tétel-törlése, a feloldója pedig a kihagyás-napló „Feloldás" művelete.
     */
    'manually_removed',
    /**
     * A HOZZÁRENDELÉS SAJÁT STÁTUSZA nem `active` (függő vagy inaktív) — a sor esedékes lett volna.
     *
     * ⚠️ EZ IS NÉMA VOLT: a generátor a billability-kapunál `continue`-zott, se számla, se napló-sor. Élesben
     * **48 `pending` hozzárendelés** áll záró dátum nélkül — ezek sosem számlázódnak, és semmi nem mondja meg,
     * miért. A `pending` LEHET szándékos (még nem indult el a szolgáltatás); a NÉMASÁG nem az.
     */
    'assignment_not_active',
    /**
     * A TULAJDONOS (ügyfél vagy fiók) INAKTÍV — nem az automata-számlázás kapcsolója, hanem a CRM-státusz.
     *
     * MIÉRT KÜLÖN OK az `auto_billing_off`-tól: más a teendő. A kapcsolót vissza lehet kapcsolni; egy inaktív
     * ügyfélnél viszont a kérdés az, hogy a sort LE KELLETT VOLNA zárni (az inaktiválás ezt ma már magától
     * megteszi — l. `clients.tx.ts` `closeOpenAssignments`), és ez a napló-sor épp a RÉGI, kézzel rendezendő
     * állapotot mutatja meg.
     */
    'owner_inactive',
    /**
     * A „NINCS HASZNÁLATI ADAT" (`no_usage_data`) PONTOSABB OKAI (user-döntés). Korábban mindhárom ugyanazt
     * mondta, holott más-más a teendő:
     *
     * `usage_not_final` — a forrás (HotelCMS jutalék-riport) a hónapot MÉG NEM ZÁRTA LE (`final: false`). A
     * részleges összeget nem számlázzuk; a teendő a HotelCMS-ben zárni — utána a napi lekérés (04:30) magától
     * beírja, és a generálás pótolja.
     */
    'usage_not_final',
    /**
     * `usage_source_disabled` — a forrásban ez az ÁG vagy MODUL KI VAN KAPCSOLVA (pl. a RoomSome AK-ág, a voucher
     * modul), ezért arra a végpont nem ad összeget. A teendő: a forrásban bekapcsolni, vagy a hozzárendelést lezárni.
     */
    'usage_source_disabled',
    /**
     * `usage_combined_minimum` — ágankénti RoomSome-tétel (direkt / ajánlatkérés külön), de a szálláshely a
     * HotelCMS-ben ÖSSZEVONT minimummal zár: ott a két ág nyers összeg, és a számlázandó a `max(direkt, ajánlat,
     * közös minimum)` — ezt csak a modul-végösszeg hordozza. A teendő: a hozzárendelést Direkt + AK-ra
     * (`rs_direct_referral`) állítani.
     */
    'usage_combined_minimum',
    /**
     * `missing_usage_ref` — a fiókon NINCS a forráshoz kellő integrációs azonosító (jutaléknál a HotelCMS-id, hírlevélnél
     * a MailWizz-id), tehát a mért adat nem köthető a hozzárendeléshez. A teendő: az azonosítót a fiókra felvinni.
     */
    'missing_usage_ref',
];
export const InvoiceGenerationSkipReasonSchema = z.enum(INVOICE_GENERATION_SKIP_REASONS);
/**
 * A generátor összegzése: esedékes hozzárendelések / csoportok / (elő)állított számlák + kihagyások.
 * `runId` = a futás azonosítója, amivel a kihagyás-napló sorai összefűzhetők (`dryRun` → `null`, mert a
 * próba-futás nem naplóz). A `skipped[]` a futás TELJES kihagyás-listája (az `already_invoiced`-dal együtt).
 * `invoices` = az ÚJ piszkozatok, `appended` = a meglévő nyitott piszkozathoz FŰZÖTT számlák.
 */
export const InvoiceGenerateResultSchema = z.object({
    month: z.string(),
    dryRun: z.boolean(),
    runId: z.string().nullable(),
    dueAssignments: z.number(),
    groups: z.number(),
    /** ÚJONNAN nyitott piszkozatok száma (a fűzötteket NEM tartalmazza — azok az `appended`-ben vannak). */
    invoices: z.number(),
    /**
     * A MÁR LÉTEZŐ nyitott piszkozathoz FŰZÖTT számlák száma.
     *
     * A generálás naponta fut, és egy csoport tételei több napra szóródnak (a jutalék-riport és a havi kézi
     * árfolyam napokkal a hónap 2-a után érkezik). Ha a csoportnak már van nyitott piszkozata ugyanazzal a
     * fej-aláírással, a mai tételek oda kerülnek — kiállított bizonylathoz a generátor soha nem nyúl.
     */
    appended: z.number(),
    items: z.number(),
    skipped: z.array(z.object({
        assignmentId: z.string(),
        reason: InvoiceGenerationSkipReasonSchema,
        /** Tétel-szintű kihagyásnál a tétel kulcsa (plan-/komponens-azonosító); `null` = a teljes számla. */
        itemKey: z.string().nullable().optional(),
    })),
});
/**
 * AZ ÉLES (nem-próba) GENERÁLÁS VÁLASZA: a futás ELINDULT, az eredmény még nincs meg.
 *
 * MIÉRT nem a teljes összegzés jön vissza: a generálás egy hónapra 900+ Odin-írás, és az Odin percenkénti
 * fojtása MINDEN havi futáson bekövetkezik. A kivárás percekig tart, a felület előtti proxy viszont 60 s
 * után elvágja a kérést — a szinkron út ezért csak rövid nekifutást engedhetett meg magának, és ami utána is
 * fojtott maradt, `rate_limited` kihagyásként landolt (a hónap egy része számlázatlanul). A futás így a válasz
 * UTÁN megy tovább, teljes kivárással; a `runId` már most megvan, tehát a kihagyás-napló szűrhető rá, a végéről
 * pedig a kezdeményező értesítést kap.
 */
export const InvoiceGenerateStartedSchema = z.object({
    runId: z.string(),
    month: z.string(),
});
/**
 * A KIÁLLÍTÁS opcionális dátum-felülírása.
 *
 * Üresen hagyva (vagy hiányzó body-val — így megy a TÖMEGES kiállítás) a szerver maga igazítja a dátumokat a
 * kiállítás napjára: a kelte a MAI nap lesz, a teljesítés és a fizetési határidő pedig az EREDETI eltolását
 * megőrizve mozdul vele. (Jövőbeli keltű piszkozatot nem mozdítunk — azt ember állította be.) A felület a
 * megerősítő ablakban ELŐRE kitöltve mutatja ezeket, és a kolléga felülírhatja; amit beküld, az megy ki.
 */
export const InvoiceIssueBodySchema = z.object({
    issueDate: isoDateStr.optional(),
    fulfillmentDate: isoDateStr.optional(),
    dueDate: isoDateStr.optional(),
});
/** A kihagyás-napló futás-indítója: az ütemezett cron vagy egy kolléga kézi futtatása. */
export const INVOICE_GENERATION_TRIGGERS = ['cron', 'manual'];
export const InvoiceGenerationTriggerSchema = z.enum(INVOICE_GENERATION_TRIGGERS);
/**
 * A KIHAGYÁS-NAPLÓ egy sora (`billing/invoice-generation-skips`, INTERNAL Odin-resource) + a BFF-en feloldott
 * nevek. „NYITOTT KIVÉTEL": `resolvedAt === null` → a probléma él (a legutóbbi futás is kihagyta); lezárt sor
 * = egy későbbi futásban a hozzárendelés számlára került (`resolvedRunId`). A `details` megjelenítési
 * részlet (deviza, hiányzó árfolyam-hónap, upstream státusz …) — nincs benne titok vagy PII.
 */
export const InvoiceGenerationSkipRowSchema = z.object({
    id: z.string(),
    runId: z.string(),
    trigger: InvoiceGenerationTriggerSchema,
    actorIdentityId: z.string().nullable(),
    actorName: z.string().nullable(),
    /**
     * Az indító NÉV-RÉSZEI — a NÉVSORREND a KLIENS döntése (magyarul vezetéknév elöl, más nyelven keresztnév
     * elöl). Az összefűzött `actorName` keresztnév-elöl áll, tehát magyar felületen fordítva jelent meg
     * (user-lelet: „Melinda Magyar"). `null`, ha a címtár nem ismeri — a felület ilyenkor az `actorName`-re esik
     * vissza. Ugyanez a minta, mint az audit-napló és a megszemélyesítés sorain.
     */
    actorGivenName: z.string().nullable(),
    actorFamilyName: z.string().nullable(),
    billingMonth: z.string(),
    assignmentId: z.string(),
    clientId: z.string(),
    clientName: z.string().nullable(),
    accountId: z.string().nullable(),
    accountName: z.string().nullable(),
    serviceName: z.string().nullable(),
    planName: z.string().nullable(),
    itemKey: z.string().nullable(),
    reason: InvoiceGenerationSkipReasonSchema,
    details: z.record(z.string(), z.unknown()).nullable(),
    occurredAt: z.string(),
    resolvedAt: z.string().nullable(),
    resolvedRunId: z.string().nullable(),
});
/**
 * A kihagyás-napló lista-szűrője: hónap (`YYYY-MM`; hiányzik → a BFF az aktuális budapesti hónapot veszi),
 * ok-kód, ügyfél, szabad-szöveg (ügyfél/fiók/szolgáltatás névre), és a nyitott/lezárt tengely (`state`,
 * alapból csak a nyitott kivételek). Offset-lapozás a lista-mintával.
 */
export const InvoiceGenerationSkipListQuerySchema = z.object({
    month: z
        .string()
        .regex(/^\d{4}-(0[1-9]|1[0-2])$/, 'YYYY-MM formátum kötelező')
        .optional(),
    reason: InvoiceGenerationSkipReasonSchema.optional(),
    clientId: z.string().uuid().optional(),
    q: z.string().optional(),
    /**
     * A NYITOTT / LEZÁRT tengely, HÁROM állású (a korábbi `includeResolved` bool helyett): `open` (alap) — csak
     * az élő kivételek · `resolved` — CSAK a lezártak (a hónap „mi oldódott meg" olvasata, amit a bool nem
     * tudott kifejezni) · `all` — a teljes történet.
     */
    state: z.enum(['open', 'resolved', 'all']).optional(),
    sort: z.string().optional(),
    order: z.enum(['asc', 'desc']).optional(),
    limit: z.coerce.number().int().positive().max(200).optional(),
    offset: z.coerce.number().int().nonnegative().optional(),
});
/** A kihagyás-napló lista-válasza (a `{ rows, total, nextOffset }` lista-minta). */
export const InvoiceGenerationSkipListSchema = z.object({
    skips: z.array(InvoiceGenerationSkipRowSchema),
    total: z.number(),
    nextOffset: z.number().nullable(),
});
//# sourceMappingURL=schemas.js.map